import { Component } from '@angular/core';
import { GridApi } from 'ag-grid-community';
import { NgxSpinnerService } from 'ngx-spinner';
import { LoadingGridComponent } from 'src/app/cell-renderer/loading-grid/loading-grid.component';
import { UtilsService } from 'src/app/services/utils.service';
import { CartaPorteService } from 'src/app/services/carta-porte.service';
import { CartaPorteActionsComponent } from '../carta-porte-actions/carta-porte-actions.component';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

@Component({
    selector: 'app-carta-porte-list',
    templateUrl: './carta-porte-list.component.html',
    styleUrls: ['./carta-porte-list.component.css']
})
export class CartaPorteListComponent {
    constructor(
        private apiCartaPorte: CartaPorteService,
        private utils: UtilsService,
        private spinner: NgxSpinnerService
    ) { }

    columnDefs = [
        { field: 'service_folio', headerName: 'Folio', minWidth: 10, suppressSizeToFit: true },
        {
            field: 'created_at',
            headerName: 'Fecha Creación',
            valueFormatter: (params: any) => {
                const v = params?.value;
                if (!v) return '';
                const d = new Date(v);
                if (isNaN(d.getTime())) return String(v);
                return d.toLocaleDateString('es-CO');
            },
        },
        { field: 'transport_mean.name', headerName: 'Medio Transporte' },
        { field: 'service.unit.unit', headerName: 'Unidad', cellClass: 'textEllipsis' },
        { field: 'service.unit.plates', headerName: 'Placa' },
        { 
            field: 'total_distance', 
            headerName: 'Distancia Total',
            valueFormatter: (params: any) => {
                const value = params?.value;
                return value ? `${value} km` : '';
            }
        },
        { 
            field: 'cfdi_total', 
            headerName: 'Valor de la mercancia',
            valueFormatter: (params: any) => {
                const value = params?.value;
                return value ? `$${Number(value).toLocaleString('es-CO')}` : '';
            }
        },
        { field: 'cargo_insurer.name', headerName: 'Aseguradora', cellClass:"textEllipsis" },
        { field: 'cargo_insurance_policy_number', headerName: 'Permiso SCT', cellClass: "textEllipsis"},
        {
            headerName: 'Acciones',
            minWidth: 160,
            cellRenderer: CartaPorteActionsComponent,
            sortable: false,
            filter: false,
            suppressSizeToFit: true,
            cellRendererParams: {
                parent: this,
                url_edit: '/carta-porte/update/',
                module: 'Carta Porte',
                singular_module: 'Carta Porte'
            }
        },
    ];

    public loadingCellRenderer: any = LoadingGridComponent;
    public loadingCellRendererParams: any = {
        loadingMessage: 'Espere un momento...',
    };

    public defaultColDef = {
        sortable: true,
        filter: false,
        resizable: true,
        flex: 1,
    };

    private gridApi!: GridApi;
    public rowData: any = [];

    public filterServiceId: any = '';
    public filterCreatedAt: any = '';

    public pageSize = 25;
    public pageSizeOptions: number[] = [25, 50, 100, 200];
    public currentPage = 1;
    public hasNextPage = false;

    private filtersChanged$ = new Subject<void>();

    ngOnInit(): void {
        this.filtersChanged$.pipe(debounceTime(350)).subscribe(() => {
            this.currentPage = 1;
            this.fetchData();
        });
    }

    onGridReady(params: any) {
        this.gridApi = params.api;
        this.fetchData();
    }

    onFiltersChanged() {
        this.filtersChanged$.next();
    }

    fetchData() {
        this.spinner.show();
        const params: any = { page: this.currentPage, pageSize: this.pageSize };
        if (this.filterServiceId !== null && this.filterServiceId !== undefined && this.filterServiceId !== '') {
            params.service_id = this.filterServiceId;
        }
        if (this.filterCreatedAt) {
            params.created_at = this.filterCreatedAt;
        }

        this.apiCartaPorte.list(params).subscribe((resp: any) => {
            const data = Array.isArray(resp?.data) ? resp.data : (Array.isArray(resp) ? resp : []);
            this.rowData = data;
            this.hasNextPage = data.length === this.pageSize;
            this.spinner.hide();
        }, () => {
            this.spinner.hide();
        });
    }

    clearFilters() {
        this.filterServiceId = '';
        this.filterCreatedAt = '';
        this.currentPage = 1;
        this.onFiltersChanged();
    }

    onPageSizeChanged(newSize: number | string) {
        const size = typeof newSize === 'string' ? parseInt(newSize, 10) : newSize;
        if (!isNaN(size)) {
            this.pageSize = size;
            this.currentPage = 1;
            this.fetchData();
        }
    }

    nextPage() {
        if (this.hasNextPage) {
            this.currentPage += 1;
            this.fetchData();
        }
    }

    prevPage() {
        if (this.currentPage > 1) {
            this.currentPage -= 1;
            this.fetchData();
        }
    }

    refreshGrid() {
        this.fetchData();
    }

    actionDelete(id: number) {
        this.utils.alertSwalConfir({ title: 'Eliminación', text: 'Esta seguro que desea eliminar la carta porte seleccionada', icon: 'warning', btn_confirm: 'Eliminar' })
            .then((result) => {
                if (result.isConfirmed) {
                    this.spinner.show();
                    this.apiCartaPorte.delete(id).subscribe(() => {
                        this.spinner.hide();
                        this.utils.alertSwal({ title: 'Operación Exitosa', text: 'Se ha eliminado la carta porte de forma exitosa', icon: 'success' });
                        this.onGridReady({ api: this.gridApi });
                    }, () => {
                        this.spinner.hide();
                        this.utils.alertSwal({ title: 'Error', text: 'Se ha presentado un error al intentar eliminar la carta porte', icon: 'error' });
                    });
                }
            });
    }

    actionStamp(serviceId: number) {
        this.spinner.show();
        this.apiCartaPorte.stampCfdi(serviceId).subscribe((resp: any) => {
            this.spinner.hide();
            this.utils.alertSwal({ title: 'Exitoso', text: resp?.msg || 'Timbrado realizado', icon: 'success' });
        }, (err: any) => {
            this.spinner.hide();
            const msg = err?.error ? (err?.error?.conectia_response?.message || JSON.stringify(err.error)) : 'Timbrado no disponible';
            this.utils.alertSwal({ title: 'Advertencia', text: msg, icon: 'error' });
        });
    }

    actionDownload(id: number) {
        if (!id) {
            return;
        }

        this.spinner.show();
        this.apiCartaPorte.downloadCfdiXml(id).subscribe((xml: string) => {
            this.spinner.hide();

            const blob = new Blob([xml], { type: 'application/xml' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `cfdi-carta-porte-${id}.xml`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        }, (err: any) => {
            this.spinner.hide();
            const msg = err?.error?.message || 'No se pudo descargar el XML para esta carta porte';
            this.utils.alertSwal({ title: 'Advertencia', text: msg, icon: 'warning' });
        });
    }

    actionDownloadPdf(id: number) {
        if (!id) {
            return;
        }

        this.spinner.show();
        this.apiCartaPorte.downloadPdf(id).subscribe((blob: Blob) => {
            this.spinner.hide();

            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `cfdi-carta-porte-${id}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        }, (err: any) => {
            this.spinner.hide();
            let msg = 'No se pudo generar el PDF para esta carta porte';
            if (err?.error instanceof Blob) {
                err.error.text().then((text: string) => {
                    try {
                        const parsed = JSON.parse(text);
                        this.utils.alertSwal({ title: 'Advertencia', text: parsed?.message || msg, icon: 'warning' });
                    } catch (e) {
                        this.utils.alertSwal({ title: 'Advertencia', text: msg, icon: 'warning' });
                    }
                });
            } else {
                msg = err?.error?.message || msg;
                this.utils.alertSwal({ title: 'Advertencia', text: msg, icon: 'warning' });
            }
        });
    }
}
